"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CANCELLATION_HOLD_STATUSES = exports.CancellationRecordSchema = exports.CancellationSettlementSchema = exports.SettlementLineSchema = exports.SETTLEMENT_GROUPS = exports.CANCELLATION_STATUSES = exports.CANCELLATION_EXITS = exports.ALL_STAGES_V1 = exports.CANCELLATION_STAGES_V1 = void 0;
exports.stageById = stageById;
exports.settlementTotals = settlementTotals;
exports.cancellationHolds = cancellationHolds;
exports.cancellationStageState = cancellationStageState;
/**
 * CANCELLATION — eight more stages on the journey the order already has.
 *
 * Not a second state machine. A cancellation is the same order's life
 * continuing, so it renders on the same rail, is read with the same clock, and
 * resolves through the same `resolveJourney`.
 *
 * The one structural decision: `setOrderStatus('cancelled')` runs at
 * `cx_closed`, NOT at `cx_raised`. Today it is the opening move and
 * `cancelledOrderProblem` makes it irreversible from that instant, so a
 * customer who changes their mind on day two needs a brand new order. Holding
 * first and cancelling last costs nothing and keeps the door open for exactly
 * as long as the conversation does.
 */
const orderJourney_1 = require("./orderJourney");
exports.CANCELLATION_STAGES_V1 = [
    {
        id: 'cx_raised', label: 'Cancellation raised', owner: 'customer', completion: 'evidence',
        blocks: ['dispatch', 'billing'],
        hint: 'Two doors, one path: the customer requests, or we initiate. Same stage either way.',
    },
    {
        id: 'cx_thread', label: 'Conversation', owner: 'rubytill', completion: 'evidence',
        hint: 'Stays open until the cancellation closes, like `live`.',
    },
    {
        id: 'cx_policy', label: 'Policy resolved', owner: 'system', completion: 'evidence',
        hint: 'The signed policy, or the honest absence of one — in which case every penalty is 0.',
    },
    {
        id: 'cx_settlement', label: 'Settlement', owner: 'rubytill', completion: 'manual',
        blocks: ['dispatch', 'billing'],
        hint: 'Nothing is cancelled until this closes. Three ways out, and one of them keeps the order.',
    },
    {
        id: 'cx_accepted', label: 'Customer accepts', owner: 'customer', completion: 'evidence',
        hint: 'Their acceptance carries the settlement VERSION it accepted; a changed number needs a new one.',
    },
    {
        id: 'cx_refund', label: 'Refund issued', owner: 'system', completion: 'evidence',
    },
    {
        id: 'cx_unwind', label: 'Unwinding', owner: 'warehouse', completion: 'manual',
        hint: 'Manual because the system genuinely does not know: stranded units, rentals owed back.',
    },
    {
        id: 'cx_closed', label: 'Cancellation closed', owner: 'system', completion: 'evidence',
        hint: 'Terminal. Only here does the order actually become cancelled.',
    },
];
/**
 * Every stage the platform knows about, order stages first.
 *
 * journey-service validates a manual completion against this rather than
 * `ORDER_STAGES_V1` alone — otherwise marking `cx_unwind` done would be
 * refused as "this journey does not carry that stage".
 */
exports.ALL_STAGES_V1 = [
    ...orderJourney_1.ORDER_STAGES_V1,
    ...exports.CANCELLATION_STAGES_V1,
];
function stageById(id) {
    return exports.ALL_STAGES_V1.find((s) => s.id === id);
}
const zod_1 = require("zod");
const journeyRecord_1 = require("./journeyRecord");
/**
 * THE THREE WAYS OUT, before anything is cancelled.
 *
 * `solution` is the one that is not an ending: it is a counter-offer that keeps
 * the order alive, so no money moves and the downstream stages are WITHDRAWN
 * rather than completed.
 */
exports.CANCELLATION_EXITS = ['penalty', 'forgive', 'solution'];
exports.CANCELLATION_STATUSES = ['open', 'proposed', 'accepted', 'withdrawn', 'closed'];
exports.SETTLEMENT_GROUPS = ['software', 'devices', 'upfront'];
/**
 * ONE LINE OF THE WORKSHEET — a package, a device, a fee.
 *
 * `penaltyCents` defaults to 0 and `ruleLabel` to "No policy · 0". That is not
 * a placeholder waiting for the policy engine: almost every live order has no
 * signed cancellation policy and never will, because nothing retroactively
 * signs one. Zero is the permanent, honest answer for those orders, and
 * `overriddenBy` is what records that a person — not a rule — set the number.
 */
exports.SettlementLineSchema = zod_1.z.object({
    lineId: zod_1.z.string().min(1).max(80),
    group: zod_1.z.enum(exports.SETTLEMENT_GROUPS),
    label: zod_1.z.string().min(1).max(160),
    detail: zod_1.z.string().max(300).optional(),
    paidCents: zod_1.z.number().int().min(0),
    refundableCents: zod_1.z.number().int().min(0),
    penaltyCents: zod_1.z.number().int().min(0).default(0),
    /** Still owed after cancelling — a financed balance. SHOWN, never charged here. */
    owedCents: zod_1.z.number().int().min(0).default(0),
    ruleLabel: zod_1.z.string().max(120).default('No policy · 0'),
    /** Set when a human typed over the auto-filled penalty. */
    overriddenBy: journeyRecord_1.ActorSchema.optional(),
});
exports.CancellationSettlementSchema = zod_1.z.object({
    /** Monotonic. A changed number is a NEW version needing a new acceptance. */
    version: zod_1.z.number().int().min(1),
    exit: zod_1.z.enum(exports.CANCELLATION_EXITS),
    lines: zod_1.z.array(exports.SettlementLineSchema).max(200).default([]),
    note: zod_1.z.string().max(2000).optional(),
    proposedBy: journeyRecord_1.ActorSchema.optional(),
    proposedAt: zod_1.z.number().optional(),
});
exports.CancellationRecordSchema = zod_1.z.object({
    cancellationId: zod_1.z.string().min(1).max(120),
    journeyId: zod_1.z.string().min(1).max(120),
    tenantId: zod_1.z.string().min(1).max(120),
    orderId: zod_1.z.string().min(1).max(120),
    status: zod_1.z.enum(exports.CANCELLATION_STATUSES),
    /** Which door it came through. The path after it is identical. */
    origin: zod_1.z.enum(['customer', 'rubytill']),
    reason: zod_1.z.string().max(2000).optional(),
    raisedBy: journeyRecord_1.ActorSchema,
    raisedAt: zod_1.z.number(),
    /** Append-only, like everything else here. The last one is what stands. */
    settlements: zod_1.z.array(exports.CancellationSettlementSchema).max(50).default([]),
    updatedAt: zod_1.z.number().optional(),
    withdrawnAt: zod_1.z.number().optional(),
    acceptedAt: zod_1.z.number().optional(),
    /**
     * WHICH settlement they accepted. A later settlement is a new proposal
     * needing a new acceptance — without this, a figure could change under an
     * acceptance that already happened.
     */
    acceptedSettlementVersion: zod_1.z.number().int().positive().optional(),
    closedAt: zod_1.z.number().optional(),
});
/**
 * The exit decides which columns survive — which is why this is one function
 * and not three screens. `solution` moves nothing but does NOT clear the
 * balance: the order continues, so the term continues with it.
 */
function settlementTotals(s) {
    const solution = s.exit === 'solution';
    const forgive = s.exit === 'forgive';
    let refundableCents = 0, penaltyCents = 0, owedCents = 0;
    for (const l of s.lines) {
        refundableCents += solution ? 0 : l.refundableCents;
        penaltyCents += solution || forgive ? 0 : l.penaltyCents;
        owedCents += forgive ? 0 : l.owedCents;
    }
    return { refundableCents, penaltyCents, netCents: refundableCents - penaltyCents, owedCents };
}
/** While the cancellation is in one of these, the order does not move. */
exports.CANCELLATION_HOLD_STATUSES = ['open', 'proposed', 'accepted'];
/**
 * WHAT THIS CANCELLATION IS HOLDING UP, right now.
 *
 * Derived from the status, never stored. A stored "isHeld" would be a second
 * copy that drifts the first time a cancellation is withdrawn — and it is the
 * copy that would keep a shipment stopped for a customer who already changed
 * their mind.
 */
function cancellationHolds(r) {
    if (!r)
        return [];
    return exports.CANCELLATION_HOLD_STATUSES.includes(r.status) ? ['dispatch', 'billing'] : [];
}
const CX_IDS = new Set(exports.CANCELLATION_STAGES_V1.map((s) => s.id));
/**
 * RESOLVE ONE CANCELLATION STAGE from the record, or hand back `undefined` for
 * anything that is not one — which is how `resolveJourney` can call this for
 * every stage without knowing which are cancellation stages.
 *
 * The manual stages (`cx_settlement`, `cx_unwind`) are NOT handled here:
 * `resolveJourney` already resolves any `completion: 'manual'` stage from
 * `ctx.manual`, and duplicating that would give one stage two answers.
 */
function cancellationStageState(def, ctx) {
    if (!CX_IDS.has(def.id) || def.completion === 'manual')
        return undefined;
    const blocksDispatch = !!def.blocks?.includes('dispatch');
    const c = ctx.cancellation;
    const todo = (detail) => ({ def, state: 'todo', blocksDispatch, detail });
    if (!c)
        return todo('No cancellation on this order.');
    // A withdrawal is not an erasure: what happened stays done, what was going to
    // happen never will. `skipped` is the state that says "this road closed",
    // and a skipped stage blocks nothing — which is what un-holds the order.
    const dead = c.status === 'withdrawn';
    const closed = c.status === 'closed';
    switch (def.id) {
        case 'cx_raised':
            return { def, state: 'done', blocksDispatch: blocksDispatch && !dead && !closed, at: c.raisedAt,
                evidence: 'cancellation record',
                detail: c.origin === 'customer' ? 'Requested by the customer.' : 'Opened by RubyTill.' };
        case 'cx_thread':
            return { def, state: 'done', blocksDispatch: false, at: c.raisedAt, evidence: 'cancellation thread',
                detail: dead || closed ? 'Closed with the cancellation.' : 'Open — stays open until this closes.' };
        case 'cx_policy':
            return { def, state: 'done', blocksDispatch: false, at: c.raisedAt,
                evidence: c.policyEnvelopeId ? 'signed policy' : 'no policy attached',
                detail: c.policyEnvelopeId
                    ? 'Resolved from the signed policy.'
                    : 'No policy attached — every penalty starts at 0 and a person decides.' };
        case 'cx_accepted':
            if (dead)
                return { def, state: 'skipped', blocksDispatch: false, detail: 'Withdrawn before acceptance.' };
            return c.acceptedAt
                ? { def, state: 'done', blocksDispatch: false, at: c.acceptedAt, evidence: 'cancellation record',
                    detail: 'The customer accepted the settlement.' }
                : todo(c.status === 'proposed' ? 'Waiting on the customer.' : 'No settlement proposed yet.');
        case 'cx_refund':
            if (dead)
                return { def, state: 'skipped', blocksDispatch: false, detail: 'Withdrawn — no money moves.' };
            if (c.refundableCents === 0) {
                return { def, state: 'skipped', blocksDispatch: false, detail: 'Nothing refundable on this settlement.' };
            }
            // NOT built yet — Plan 3. Saying "todo" is honest; saying "done" would be a lie
            // on the one stage that moves money.
            return todo('Refunds are not issued from here yet.');
        case 'cx_closed':
            if (dead)
                return { def, state: 'skipped', blocksDispatch: false, detail: 'Withdrawn — the order continues.' };
            return closed
                ? { def, state: 'done', blocksDispatch: false, at: c.closedAt, evidence: 'cancellation record',
                    detail: 'Cancelled. The order does not return to the pipeline.' }
                : todo('Unwind first, then close.');
        default:
            return undefined;
    }
}
// WARNING: this call is what wires all six `cx_*` stages into
// `resolveJourney` — it is a side effect of importing this module, not
// something anything explicitly calls. If `package.json` ever declares
// `"sideEffects": false`, a bundler may legally drop this call as
// "unused", and every `cx_*` stage would silently regress to `todo` while
// the server still holds the order. Keep `sideEffects` unset (or list this
// file) if that flag is ever added.
const orderJourney_2 = require("./orderJourney");
(0, orderJourney_2.registerCancellationResolver)(cancellationStageState);
//# sourceMappingURL=cancellation.js.map